'use client'

import type { GraphQLResult } from 'aws-amplify/api'
import { useQuery } from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  fetchSupportPayload,
  useSupportTarget,
} from '@/services/support/support.client'
import {
  supportQueryKey,
  type SupportTarget,
} from '@/services/support/supportContract'
import { useSessionRuntime } from '@/context/SessionRuntimeContext'
import { getGridViewConfigQuery } from '@/services/graphql/gridView'
import type {
  GridViewConfigItem,
  GridViewItem,
  GridViewPreferenceItem,
} from '@/services/gridView/gridView.types'
import {
  type GridViewPageFetcher,
  listGridViewsWithFetcher,
} from './gridView.shared'
import { gridViewKeys } from './gridView.keys'
import {
  gridViewPreferenceQueryPreset,
  gridViewQueryPreset,
  referenceQueryPreset,
} from '@/services/query-presets'

const toUserGridViewState = (
  item: GridViewConfigItem | null | undefined,
): GridViewPreferenceItem | null => {
  if (!item || item.kind !== 'USER_STATE' || !item.userID) return null
  if (item.status === 'deleted') return null

  return {
    id: item.id,
    userID: item.userID,
    organizationID: item.organizationID,
    scopeKey: item.scopeKey,
    selectedViewRef: item.selectedViewRef,
    draftPayload: item.draftPayload,
    payloadVersion: item.payloadVersion,
    gridSchemaVersion: item.gridSchemaVersion,
    status: item.status,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    revision: item.revision ?? 0,
    source: item.source,
  }
}

const toTeamGridView = (
  item: GridViewConfigItem | null | undefined,
): GridViewItem | null => {
  if (
    !item ||
    item.kind !== 'TEAM_VIEW' ||
    item.status === 'deleted' ||
    !item.name ||
    !item.payload ||
    !item.createdByUserID
  ) {
    return null
  }

  return {
    id: item.id,
    organizationID: item.organizationID,
    scopeKey: item.scopeKey,
    name: item.name,
    description: item.description ?? null,
    payload: item.payload,
    payloadVersion: item.payloadVersion,
    gridSchemaVersion: item.gridSchemaVersion,
    status: item.status,
    isDefault: item.isDefault ?? false,
    createdByUserID: item.createdByUserID,
    updatedByUserID: item.updatedByUserID ?? item.createdByUserID,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    source: item.source,
  }
}

export const createGridViewPageFetcher = (
  supportTarget?: SupportTarget | null,
  signal?: AbortSignal,
): GridViewPageFetcher => {
  return async ({ query, variables }) => {
    if (supportTarget) {
      const page = await fetchSupportPayload<{
        items: GridViewConfigItem[]
        nextToken?: string | null
      }>(
        `query ConsoleSupportGridViews($targetUserID: ID!, $organizationID: ID!, $scopeKey: String!, $nextToken: String) {
          consoleSupportGridViews(targetUserID: $targetUserID, organizationID: $organizationID, scopeKey: $scopeKey, nextToken: $nextToken) { payload }
        }`,
        'consoleSupportGridViews',
        {
          targetUserID: supportTarget.targetUserID,
          organizationID: variables.organizationID,
          scopeKey: variables.scopeKey,
          nextToken: variables.nextToken,
        },
        signal,
      )
      if (
        !Array.isArray(page?.items) ||
        page.items.some(
          (item) =>
            item.kind !== 'TEAM_VIEW' ||
            item.organizationID !== variables.organizationID ||
            item.scopeKey !== variables.scopeKey,
        )
      ) {
        throw new Error('Support grid view scope could not be confirmed')
      }
      return { items: page.items, nextToken: page.nextToken ?? null }
    }
    const client = getAmplifyClient()
    const result = (await client.graphql({
      query,
      variables,
    })) as GraphQLResult<{
      [key: string]: { items: GridViewConfigItem[]; nextToken: string | null }
    }>
    if (result.errors?.length) {
      throw new Error('Failed to load grid views')
    }

    const data = result.data
    const key = Object.keys(data ?? {}).find(
      (itemKey) => data?.[itemKey]?.items,
    )
    const page = key ? data?.[key] : null
    if (!page || !Array.isArray(page.items)) {
      throw new Error('Failed to load grid views')
    }

    return { items: page.items, nextToken: page.nextToken ?? null }
  }
}

export const listGridViews = async ({
  organizationID,
  scopeKey,
  fetchPage = createGridViewPageFetcher(),
}: {
  organizationID: string
  scopeKey: string
  fetchPage?: GridViewPageFetcher
}) => {
  return listGridViewsWithFetcher({
    organizationID,
    scopeKey,
    fetchPage,
  })
}

export const useOrganizationGridViews = ({
  organizationID,
  scopeKey,
  enabled = true,
}: {
  organizationID?: string
  scopeKey?: string
  enabled?: boolean
}) => {
  const supportTarget = useSupportTarget()
  return useQuery({
    ...gridViewQueryPreset,
    queryKey: supportQueryKey(
      gridViewKeys.list(organizationID, scopeKey),
      supportTarget,
    ),
    queryFn: ({ signal }) =>
      listGridViews({
        organizationID: organizationID!,
        scopeKey: scopeKey!,
        fetchPage: createGridViewPageFetcher(supportTarget, signal),
      }),
    enabled: enabled && Boolean(organizationID && scopeKey),
  })
}

export const getGridViewPreference = async (
  id: string,
): Promise<GridViewPreferenceItem | null> => {
  const client = getAmplifyClient()
  const { data } = (await client.graphql({
    query: getGridViewConfigQuery,
    variables: { id },
  })) as GraphQLResult<{
    getGridViewConfig: GridViewConfigItem | null
  }>
  return toUserGridViewState(data?.getGridViewConfig)
}

export const getGridView = async (id: string): Promise<GridViewItem | null> => {
  const client = getAmplifyClient()
  const { data } = (await client.graphql({
    query: getGridViewConfigQuery,
    variables: { id },
  })) as GraphQLResult<{
    getGridViewConfig: GridViewConfigItem | null
  }>
  return toTeamGridView(data?.getGridViewConfig)
}

export const useGridView = ({
  id,
  enabled = true,
  organizationID,
  scopeKey,
}: {
  id?: string
  enabled?: boolean
  organizationID?: string
  scopeKey?: string
}) => {
  const supportTarget = useSupportTarget()
  return useQuery({
    ...referenceQueryPreset,
    queryKey: supportQueryKey(
      gridViewKeys.detail(id),
      supportTarget,
      organizationID,
      scopeKey,
    ),
    queryFn: async ({ signal }) => {
      if (!supportTarget) return getGridView(id!)
      if (!organizationID || !scopeKey)
        throw new Error('Support view scope is required')
      const views = await listGridViews({
        organizationID,
        scopeKey,
        fetchPage: createGridViewPageFetcher(supportTarget, signal),
      })
      return views.find((view) => view.id === id) ?? null
    },
    enabled: enabled && Boolean(id),
    placeholderData: null,
  })
}

export const useGridViewPreference = ({
  id,
  enabled = true,
}: {
  id?: string
  enabled?: boolean
}) => {
  const { isImpersonating, sessionScopeKey } = useSessionRuntime()
  return useQuery({
    ...gridViewPreferenceQueryPreset,
    queryKey: gridViewKeys.preference(id, isImpersonating, sessionScopeKey),
    queryFn: () =>
      isImpersonating
        ? getImpersonatedGridViewPreference(id!)
        : getGridViewPreference(id!),
    enabled: enabled && Boolean(id),
    placeholderData: null,
  })
}

export const getImpersonatedGridViewPreference = async (
  id: string,
): Promise<GridViewPreferenceItem | null> => {
  const response = await fetch(
    `/api/grid-views/impersonated-preference?${new URLSearchParams({ id })}`,
    { cache: 'no-store' },
  )
  if (!response.ok) throw new Error('Failed to load saved view')
  const result = await response.json()
  if (!Object.prototype.hasOwnProperty.call(result, 'preference')) {
    throw new Error('Failed to load saved view')
  }
  if (
    result.preference !== null &&
    (!result.preference ||
      result.preference.id !== id ||
      result.preference.kind !== 'USER_STATE' ||
      !result.preference.userID)
  ) {
    throw new Error('Failed to load saved view')
  }
  return toUserGridViewState(result.preference)
}
